// src/pages/GeckosPage.js
import React, { useEffect, useState } from 'react';
import { getProducts } from '../services/api';
// S3-01.3 follow-up (2026-08-30): ProductGrid IGNORES its products prop
// (self-fetches the WHOLE catalog with its own filter chrome) — it put 17
// frogs on this page the moment the first product of this type existed.
// Render ProductCards directly, the FrogsPage pattern.
import ProductCard from '../components/Products/ProductCard';
import SpeciesLearnGrid from '../components/Products/SpeciesLearnGrid';
import { Container, Row, Col } from 'react-bootstrap';
import SEO from '../components/SEO';
import { GECKOS_KEYWORDS } from '../constants/seoKeywords';
import FroggySpinner from '../components/common/FroggySpinner';
import EmptyStateWayOn from '../components/common/EmptyStateWayOn';
import '../styles/products.css';
import '../styles/ff-empty-state.css';

const GeckosPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinguish "loaded, empty list" from "load failed" — same
  // 2026-07-02 fix as FrogsPage. Silent try/catch was making the
  // "we're building our Gecko inventory" copy show up for network
  // errors too.
  const [loadError, setLoadError] = useState(null);

  const fetchAvailableGeckos = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const filters = {
        speciesType: 3, // GECKO (S3-01.1 taxonomy; backend filters live)
        isForSale: true,
        featuredFirst: true, // same merch ordering as /frogs + homepage rail
      };
      const response = await getProducts(filters);
      setProducts(response);
    } catch (error) {
      console.error('[GeckosPage] load failed:', error?.message || error);
      setLoadError(
        error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')
          ? 'Having trouble reaching our inventory just now. Please try again in a moment.'
          : 'Something went wrong loading available geckos. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableGeckos();
  }, []);

  return (
    <>
      <SEO
        title="Select Geckos for Sale — Leachianus & Leopard Geckos"
        description="Captive-bred select geckos: Rhacodactylus leachianus (Yate), Black Night and Korean-line Mandarin leopard geckos. Live-arrival guaranteed."
        canonical="https://feelingfroggy.llc/geckos"
        keywords={GECKOS_KEYWORDS}
      />
      <Container className="products-page">
        <h1 className="ff-heading-h1">Available Now – Geckos</h1>
        {loading ? (
          <FroggySpinner size="lg" />
        ) : loadError ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">{loadError}</p>
            <button
              type="button"
              className="ff-btn ff-btn--secondary"
              onClick={fetchAvailableGeckos}
            >
              Try again
            </button>
          </div>
        ) : products.length === 0 ? (
          /* #103 zero-inventory graceful degradation, gecko edition
             (2026-08-30): species cards with care-guide links instead of
             a dead end — same LEARN mode FrogsPage ships. */
          <SpeciesLearnGrid
            speciesType={3}
            wayOn={<EmptyStateWayOn />}
            fallback={(
              <div className="ff-empty-state">
                <img
                  src="/logos/ff_original_96px.png"
                  alt=""
                  aria-hidden="true"
                  className="ff-empty-state__logo"
                />
                <h3 className="ff-empty-state__title">We're building our Gecko inventory</h3>
                <p className="ff-empty-state__message">Captive-bred geckos coming soon. Check back for updates.</p>
                <EmptyStateWayOn />
              </div>
            )}
          />
        ) : (
          <Row className="g-4">
            {products.map((product, productIdx) => (
              <Col key={product.id} xs={12} sm={6} md={4}>
                <ProductCard product={product} eagerImage={productIdx < 6} />
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </>
  );
};

export default GeckosPage;
